import { useEffect } from "react";

/**
 * Adds dir="auto" to text-bearing elements present on the page
 * and to any that appear later (via MutationObserver).
 */
export function useDirAuto({
  // tags that actually hold text (faster than "*")
  selector = "p, span, div, h1, h2, h3, h4, h5, h6, li, td, th, a, button, label, blockquote, figcaption, summary, caption, input"
} = {}) {
  useEffect(() => {
    const apply = (root) => {
      if (root.nodeType === 1 /* ELEMENT_NODE */) {
        const el = /** @type {HTMLElement} */ (root);
        if (el.matches && el.matches(selector) && !el.hasAttribute("dir")) {
          el.setAttribute("dir", "auto");
        }
        if (el.firstChild && !el.hasAttribute("dir") && el.matches?.("div,span,p")) {
          // Optional: set on common wrappers that contain text nodes
          el.setAttribute("dir", "auto");
        }
      }
      if (root.querySelectorAll) {
        root.querySelectorAll(selector).forEach((el) => {
          if (!el.hasAttribute("dir")) el.setAttribute("dir", "auto");
        });
      }
    };

    // initial pass
    apply(document.body);

    // observe additions/changes
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // New elements added
        m.addedNodes.forEach((node) => apply(node));
        // If only text changed, ensure the parent has dir="auto"
        if (m.type === "characterData" && m.target.parentElement) {
          const parent = m.target.parentElement;
          if (!parent.hasAttribute("dir") && parent.matches("div,span,p,li,td,th")) {
            parent.setAttribute("dir", "auto");
          }
        }
      }
    });

    obs.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => obs.disconnect();
  }, [selector]);
}